/**
 * 订单详情
 * 状态进度条 + 订单/金额/凭证/合同/存证 + 资金托管流水 + 操作区 + 评价弹窗
 * 联调对齐（M2-2 遗留 #4）：
 *  - 订单状态机对齐后端 0待付款/1已付款/2已发货/3已完成/4已取消/5已退款（移除仲裁态）；
 *  - 支付为 PUT /orders/:id/pay；
 *  - 评价入口：完成后 15 天内（IR-15），提交 POST /reviews；
 *  - 新增资金托管流水（GET /escrow/orders/:order_id、/escrow/transactions）；
 *  - 电子合同接入 GET /contracts/orders/:order_id（未生成时降级为订单数据预览）。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  cancelOrder,
  closeAfterSales,
  completeOrder,
  createReview,
  downloadContractText,
  getContractByOrder,
  getEscrowOverview,
  getOrder,
  listEscrowTransactions,
  openAfterSales,
  payOrder,
  shipOrder,
} from '@/api/trade';
import { getTransitByOrder } from '@/api/transit';
import { ApiError } from '@/api/http';
import { useAuth } from '@/stores/auth';
import { paymentErrorKind, paymentErrorText } from '@/utils/paymentPassword';
import type {
  ContractRecord,
  EscrowStatistics,
  EscrowTransaction,
  ProofItem,
  TradeOrder,
  TransitOrder,
} from '@/types';
import {
  DELIVERY_METHOD,
  LOCK_MINUTES,
  ORDER_TYPE,
  PAYMENT_METHOD,
  REVIEW_DAYS,
  cosUrl,
  imgFallback,
  isOrderSettled,
} from '@/utils/constants';
import { formatDateTime, formatMoney, formatWeight, maskPhone } from '@/utils/format';
import StatusProgress from '@/components/common/StatusProgress';
import StatusTag from '@/components/common/StatusTag';
import Countdown from '@/components/common/Countdown';
import ProofUpload from '@/components/common/ProofUpload';
import Loading from '@/components/common/Loading';
import ErrorBox from '@/components/common/ErrorBox';
import ContractPreview from '@/components/common/ContractPreview';
import PaymentPasswordModal from '@/components/common/PaymentPasswordModal';
import '@/styles/pages/order.css';

const daysAfter = (dateStr: string | null | undefined, days: number): Date => {
  const d = dateStr ? new Date(dateStr) : new Date();
  return new Date(d.getTime() + days * 86400000);
};

// 资金托管状态字典
const ESCROW_STATUS: Record<number, { label: string; tone: 'default' | 'success' | 'danger' | 'info' | 'warning' | 'gold' | 'teal' }> = {
  0: { label: '未托管', tone: 'default' },
  1: { label: '已冻结', tone: 'info' },
  2: { label: '已解付', tone: 'success' },
  3: { label: '已退款', tone: 'warning' },
};

// 账务流水类型字典
const TRANS_TYPE: Record<number, string> = {
  1: '冻结',
  2: '解付',
  3: '退款',
  4: '服务费',
  5: '入账',
  6: '出账',
};

// 合同状态字典
const CONTRACT_STATUS: Record<number, { label: string; tone: 'default' | 'success' | 'danger' | 'info' | 'warning' | 'gold' | 'teal' }> = {
  0: { label: '未签署', tone: 'default' },
  1: { label: '单方已签', tone: 'info' },
  2: { label: '双方已签（生效）', tone: 'success' },
  3: { label: '已作废', tone: 'danger' },
};

// 分转元
const fmtCents = (v?: number | null) => (v == null ? '-' : `¥${(Number(v) / 100).toFixed(2)}`);

export default function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const orderId = Number(id);
  const { user } = useAuth();

  const [order, setOrder] = useState<TradeOrder | null>(null);
  const [transit, setTransit] = useState<TransitOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionMsg, setActionMsg] = useState('');
  const [actionError, setActionError] = useState('');
  const [proofs, setProofs] = useState<ProofItem[]>([]);
  const [showContract, setShowContract] = useState(false);

  // 支付弹层（资金 2FA · 支付密码，N3 前端闭环）
  const [showPay, setShowPay] = useState(false);
  const [payError, setPayError] = useState('');
  const [payPwdNotSet, setPayPwdNotSet] = useState(false);

  // 资金托管
  const [escrow, setEscrow] = useState<EscrowStatistics | null>(null);
  const [escrowTx, setEscrowTx] = useState<EscrowTransaction[]>([]);
  const [escrowLoading, setEscrowLoading] = useState(false);
  // 电子合同（后端记录；未生成时降级订单数据预览）
  const [contract, setContract] = useState<ContractRecord | null>(null);
  const [contractLoading, setContractLoading] = useState(false);
  // 评价弹窗
  const [showReview, setShowReview] = useState(false);
  const [rating, setRating] = useState(5);
  const [reviewContent, setReviewContent] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  // 售后弹窗（P0-5：已完成 → 售后中；买家 15 天窗口内发起）
  const [showAfterSales, setShowAfterSales] = useState(false);
  const [afterSalesReason, setAfterSalesReason] = useState('');
  const [afterSalesSubmitting, setAfterSalesSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, t] = await Promise.all([
        getOrder(orderId),
        getTransitByOrder(orderId).catch(() => null),
      ]);
      setOrder(o);
      setTransit(t);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  const loadEscrow = useCallback(async () => {
    setEscrowLoading(true);
    try {
      const [stats, tx] = await Promise.all([
        getEscrowOverview(orderId).catch(() => null),
        listEscrowTransactions({ order_id: orderId, page: 1, page_size: 50 }).catch(() => null),
      ]);
      setEscrow(stats);
      setEscrowTx(tx?.list ?? []);
    } catch {
      // 托管接口异常不阻塞页面
    } finally {
      setEscrowLoading(false);
    }
  }, [orderId]);

  const loadContract = useCallback(async () => {
    setContractLoading(true);
    try {
      const c = await getContractByOrder(orderId);
      setContract(c);
    } catch {
      setContract(null); // 未生成合同 → 降级订单数据预览
    } finally {
      setContractLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    load();
    loadEscrow();
    loadContract();
  }, [load, loadEscrow, loadContract]);

  const isBuyer = user?.id === order?.buyer_id;
  const isSeller = user?.id === order?.seller_id;

  // 评价入口：完成后 15 天内（IR-15；后端 3=已完成）
  const reviewOpen = useMemo(() => {
    if (!order || order.order_status !== 3) return false;
    return new Date() <= daysAfter(order.completed_at, REVIEW_DAYS);
  }, [order]);

  const item = order?.items?.[0];
  const cattle = item?.cattle;

  // 确认支付（资金 2FA：携带支付密码；2004 未设置引导 / 2006 重试 / 2007 锁定按码区分）
  const handlePay = async (paymentPassword: string) => {
    setBusy(true);
    setActionError('');
    setPayError('');
    setPayPwdNotSet(false);
    try {
      await payOrder(orderId, paymentPassword);
      setActionMsg('支付成功，资金已进入平台监管账户');
      setShowPay(false);
      await Promise.all([load(), loadEscrow()]);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : 0;
      const msg = e instanceof Error ? e.message : '支付失败';
      setPayError(paymentErrorText(code, msg));
      if (paymentErrorKind(code) === 'not_set') setPayPwdNotSet(true);
    } finally {
      setBusy(false);
    }
  };

  const openPayModal = () => {
    setPayError('');
    setPayPwdNotSet(false);
    setShowPay(true);
  };

  const handleCancel = async () => {
    if (!window.confirm('确定取消该订单吗？牛只将被释放。')) return;
    setBusy(true);
    setActionError('');
    try {
      await cancelOrder(orderId, '买家取消');
      setActionMsg('订单已取消，牛只已释放');
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : '取消失败');
    } finally {
      setBusy(false);
    }
  };

  // 卖家安排发货（已付款 → 已发货）
  const handleShip = async () => {
    setBusy(true);
    setActionError('');
    try {
      await shipOrder(orderId);
      setActionMsg('已安排发货，请通知买家在平台确认交割');
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : '操作失败');
    } finally {
      setBusy(false);
    }
  };

  // 买家确认交割（已发货 → 已完成，联动解付）
  const handleComplete = async () => {
    if (proofs.length < 3) {
      setActionError('请上传至少 3 张交割凭证');
      return;
    }
    setBusy(true);
    setActionError('');
    try {
      const gps = proofs.find((p) => p.lng !== undefined && p.lat !== undefined);
      const noteProof = proofs.find((p) => p.note && p.note.trim());
      await completeOrder(orderId, {
        vouchers: proofs.map((p) => p.url),
        gps: gps ? { lng: gps.lng as number, lat: gps.lat as number } : undefined,
        note: noteProof?.note?.trim() || undefined,
      });
      setActionMsg('交割确认成功，平台将解付卖家');
      await Promise.all([load(), loadEscrow()]);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : '交割确认失败');
    } finally {
      setBusy(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!order) return;
    setBusy(true);
    setActionError('');
    try {
      const role = isBuyer ? 1 : 2;
      await createReview({
        order_id: orderId,
        role,
        rating,
        content: reviewContent.trim(),
      });
      setShowReview(false);
      setActionMsg('评价成功，感谢您的反馈');
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : '评价提交失败');
    } finally {
      setBusy(false);
      setReviewSubmitting(false);
    }
  };

  // A-05 下载合同文本（.txt，非 PDF；仅存在有效合同时展示入口）
  const handleDownloadContract = async () => {
    if (!contract || contract.status === 3) return;
    setBusy(true);
    setActionError('');
    try {
      await downloadContractText(orderId);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : '合同下载失败');
    } finally {
      setBusy(false);
    }
  };

  // C-01 发起运输：已完成（3）且无关联运输单 → 跳转 transport 创建页并预填 order_id/牛只数/货主
  const handleStartTransit = () => {
    if (!order) return;
    const cattleCount = (order.items ?? []).reduce((s, it) => s + Number(it.quantity ?? 1), 0);
    const url = `https://transport.yunniuji.com/enterprise/orders/new?order_id=${order.id}&cattle_count=${cattleCount}&consignor_id=${order.seller_id}`;
    window.open(url, '_blank', 'noopener');
  };

  // 买家申请售后（已完成 3 → 售后中 7；body reason 5-512 字，对齐后端 binding）
  const handleOpenAfterSales = async () => {
    const reason = afterSalesReason.trim();
    if (reason.length < 5) {
      setActionError('请填写售后原因（至少 5 个字）');
      return;
    }
    setBusy(true);
    setAfterSalesSubmitting(true);
    setActionError('');
    try {
      await openAfterSales(orderId, reason);
      setActionMsg('售后申请已提交，处理完成前订单暂停结算与评价');
      setShowAfterSales(false);
      setAfterSalesReason('');
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : '售后申请失败');
    } finally {
      setBusy(false);
      setAfterSalesSubmitting(false);
    }
  };

  // 买家撤回 / 平台关闭售后（售后中 7 → 已完成 3）
  const handleCloseAfterSales = async () => {
    if (!window.confirm('确认撤回本次售后申请？订单将回到已完成状态。')) return;
    setBusy(true);
    setActionError('');
    try {
      await closeAfterSales(orderId);
      setActionMsg('售后已撤回，订单回到已完成');
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : '操作失败');
    } finally {
      setBusy(false);
    }
  };

  if (loading && !order) {
    return (
      <div className="container order-page">
        <Loading rows={5} />
      </div>
    );
  }

  if (error && !order) {
    return (
      <div className="container order-page">
        <ErrorBox message={error} onRetry={load} />
      </div>
    );
  }

  if (!order) return null;

  // P0-5 派生标记：已结算 = Completed(3) + settled_at（后端 IsOrderSettled）
  const settled = isOrderSettled(order);
  const afterSalesOpen = order.order_status === 7;

  // 合同展示数据：优先后端合同记录，未生成则降级订单数据
  const contractStatus = contract ? CONTRACT_STATUS[contract.status] : { label: '未生成', color: 'default' };
  const contractNo = contract?.contract_no ?? order.order_no;
  const contractAmount = contract ? fmtCents(contract.amount) : formatMoney(order.total_amount);
  const contractSignTx = contract?.sign_cert_no || contract?.sign_tx_hash || '';

  return (
    <div className="order-page container">
      {actionMsg ? (
        <div className="order-tip order-tip--success" onClick={() => setActionMsg('')}>{actionMsg} ✕</div>
      ) : null}

      {/* ① 状态进度条 */}
      <div className="order-section">
        <StatusProgress status={order.order_status} settled={settled} />
      </div>

      {/* ② 订单信息 */}
      <div className="order-grid">
        <div className="order-section">
          <div className="order-card">
            <div className="order-card__head">订单信息</div>
            <div className="order-card__rows">
              <div><span>订单编号</span><b>{order.order_no}</b></div>
              <div><span>订单类型</span><b>{ORDER_TYPE[order.order_type] ?? '-'}</b></div>
              <div><span>下单时间</span><b>{formatDateTime(order.created_at)}</b></div>
              <div><span>交割方式</span><b>{DELIVERY_METHOD[order.delivery_method ?? 1] ?? '-'}</b></div>
              <div><span>交割地点</span><b>{order.delivery_address ?? '-'}</b></div>
              <div><span>交割时间</span><b>{formatDateTime(order.delivery_time)}</b></div>
              <div><span>联系人</span><b>{order.contact_name ?? '-'}（{maskPhone(order.contact_phone)}）</b></div>
              <div><span>支付方式</span><b>{PAYMENT_METHOD[order.payment_method ?? 1] ?? '-'}</b></div>
            </div>
          </div>

          <div className="order-card">
            <div className="order-card__head">牛只信息</div>
            <div className="order-cattle">
              <span className="order-cattle__thumb">
                <img
                  src={cattle?.images?.[0] ?? cattle?.face_image_url ?? cosUrl('/images/cattle-default.jpg')}
                  alt=""
                  data-local="/images/cattle-default.jpg"
                  onError={imgFallback}
                />
              </span>
              <div className="order-cattle__info">
                <strong>{cattle?.breed} · {cattle?.ear_tag_no}</strong>
                <span>{formatWeight(cattle?.weight)} · {cattle?.age_days} 日龄</span>
                <span>单价 {formatMoney(item?.unit_price)} × {item?.quantity} 头</span>
              </div>
            </div>
          </div>

          <div className="order-card">
            <div className="order-card__head">交易金额</div>
            <div className="order-card__rows">
              <div><span>挂牌价小计</span><b>{formatMoney(order.total_amount)}</b></div>
              <div><span>平台服务费</span><b>{formatMoney(order.service_fee)}</b></div>
              <div className="order-card__total"><span>实付合计</span><b>{formatMoney(order.paid_amount || order.total_amount + order.service_fee)}</b></div>
              {order.order_status === 5 ? (
                <div className="order-card__refund">退款状态：全额退款（如有违约将另行通知）</div>
              ) : null}
              {settled ? (
                <div className="order-card__settled">
                  已结算：{formatDateTime(order.settled_at)} · 平台已完成财务结算，订单款项与平台服务费已按托管规则处理
                </div>
              ) : null}
            </div>
          </div>

          {/* P0-5 售后 / 结算信息（代码口径：已结算 = Completed+settled_at；售后中 = order_status 7） */}
          {afterSalesOpen ? (
            <div className="order-card">
              <div className="order-card__head">售后信息</div>
              <div className="order-card__rows">
                <div><span>售后状态</span><b><StatusTag type="custom" label="售后中" tone="gold" /></b></div>
                <div><span>开启时间</span><b>{formatDateTime(order.after_sales_opened_at)}</b></div>
                <div><span>售后原因</span><b>{order.after_sales_reason || '-'}</b></div>
                {order.settled_at ? (
                  <div><span>结算状态</span><b>{formatDateTime(order.settled_at)} 已完成平台结算（售后仅作记录）</b></div>
                ) : null}
              </div>
              <div className="order-card__hint">处理完成前订单暂停结算与评价；如涉及退款请等待平台处理</div>
            </div>
          ) : null}

          {settled ? (
            <div className="order-card">
              <div className="order-card__head">结算信息</div>
              <div className="order-card__rows">
                <div><span>结算状态</span><b><StatusTag type="custom" label="已结算" tone="gold" /></b></div>
                <div><span>结算时间</span><b>{formatDateTime(order.settled_at)}</b></div>
              </div>
              <div className="order-card__hint">已结算订单如需售后请线下联系平台客服处理</div>
            </div>
          ) : null}
        </div>

        {/* ③ 凭证 / 合同 / 托管 / 在途 */}
        <div className="order-section">
          <div className="order-card">
            <div className="order-card__head">付款凭证</div>
            {order.payment_status === 2 ? (
              <div className="order-voucher">
                <span className="order-voucher__ok">✓ 已支付</span>
                <span>支付时间 {formatDateTime(order.paid_at)}</span>
                <span>流水号 {order.payment_no || `PAY-${order.order_no}`}</span>
              </div>
            ) : (
              <div className="order-voucher order-voucher--pending">未支付</div>
            )}
          </div>

          <div className="order-card">
            <div className="order-card__head">交割凭证（≥3 张 + GPS）</div>
            {order.delivery_vouchers && order.delivery_vouchers.length > 0 ? (
              <div className="order-vouchers">
                {order.delivery_vouchers.map((v, i) => (
                  <span className="order-voucher__thumb" key={i}>
                    {v.startsWith('data:') ? <img src={v} alt={`凭证${i + 1}`} /> : `凭证 ${i + 1}`}
                  </span>
                ))}
              </div>
            ) : (
              <div className="order-voucher order-voucher--pending">尚未上传交割凭证</div>
            )}

            {/* 买家已发货后可上传并确认交割 */}
            {isBuyer && order.order_status === 2 ? (
              <div className="order-proof">
                <ProofUpload
                  value={proofs}
                  onChange={setProofs}
                  min={3}
                  deliveryLocation={
                    order.delivery_address
                      ? { lng: 118.5, lat: 36.5, name: order.delivery_address }
                      : undefined
                  }
                />
                {proofs.length >= 3 ? (
                  <button className="btn btn-primary" disabled={busy} onClick={handleComplete}>
                    {busy ? '提交中…' : '确认交割'}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="order-card">
            <div className="order-card__head">电子合同</div>
            <div className="order-contract">
              <button className="btn btn-outline btn-sm" onClick={() => setShowContract(true)}>
                📄 查看电子合同
              </button>
              {contract && contract.status !== 3 ? (
                <button className="btn btn-outline btn-sm" disabled={busy} onClick={handleDownloadContract}>
                  {busy ? '下载中…' : '⬇ 下载合同文本'}
                </button>
              ) : null}
              <span className="order-contract__cert">
                状态：{contractStatus.label}
                {contractLoading ? '（加载中…）' : ''}
              </span>
              <span className="order-contract__cert">
                存证编号：{contractSignTx || `CERT-${order.order_no}`}
                <span className="scan-link" title="链上查验平台即将上线，敬请期待">[链上查验·即将上线]</span>
              </span>
            </div>
          </div>

          {/* 资金托管（M2-2 联调） */}
          <div className="order-card">
            <div className="order-card__head">资金托管</div>
            {escrowLoading ? (
              <div className="order-voucher order-voucher--pending">托管数据加载中…</div>
            ) : escrow ? (
              <>
                <div className="order-escrow-status">
                  托管状态：
                  <StatusTag type="custom" label={ESCROW_STATUS[escrow.escrow_status]?.label} tone={ESCROW_STATUS[escrow.escrow_status]?.tone} />
                </div>
                <div className="order-escrow-rows">
                  <div><span>冻结</span><b>{fmtCents(escrow.frozen_amount)}（{escrow.frozen_count} 笔）</b></div>
                  <div><span>解付</span><b>{fmtCents(escrow.released_amount)}（{escrow.released_count} 笔）</b></div>
                  <div><span>退款</span><b>{fmtCents(escrow.refund_amount)}（{escrow.refund_count} 笔）</b></div>
                  <div><span>平台服务费</span><b>{fmtCents(escrow.fee_amount)}</b></div>
                </div>
                {escrowTx.length > 0 ? (
                  <div className="order-escrow-tx">
                    {escrowTx.map((t) => (
                      <div className="order-escrow-tx__row" key={t.id}>
                        <span className={`order-escrow-tx__type type-${t.trans_type}`}>{TRANS_TYPE[t.trans_type] ?? t.trans_type}</span>
                        <span className="order-escrow-tx__remark">{t.remark}</span>
                        <b className={t.amount >= 0 ? 'order-escrow-tx__in' : 'order-escrow-tx__out'}>
                          {t.amount >= 0 ? '+' : ''}{fmtCents(t.amount)}
                        </b>
                        <span className="order-escrow-tx__time">{formatDateTime(t.created_at)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="order-voucher order-voucher--pending">暂无账务流水</div>
                )}
              </>
            ) : (
              <div className="order-voucher order-voucher--pending">该订单暂无托管记录（付款后自动生成）</div>
            )}
          </div>

          {/* 在途区块（C-02 真实回显；C-01 已完成订单发起运输） */}
          <div className="order-card">
            <div className="order-card__head">运输状态（在途监管）</div>
            {transit ? (
              <div className="order-transit">
                <a
                  className="order-transit__head"
                  href={`https://transport.yunniuji.com/enterprise/orders/${transit.id}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ textDecoration: 'none' }}
                >
                  <strong>{transit.transit_no}</strong>
                  <StatusTag type="transit" status={transit.status} />
                </a>
                <div className="order-transit__rows">
                  <div><span>起运地</span><b>{transit.origin_city ?? '-'}</b></div>
                  <div><span>目的地</span><b>{transit.destination_city ?? '-'}</b></div>
                  <div><span>车牌</span><b>{transit.plate_no ?? '-'}</b></div>
                  <div><span>司机</span><b>{transit.driver_name ?? '-'}（{maskPhone(transit.driver_phone)}）</b></div>
                </div>
                <div className="order-transit__tip">
                  点击单号前往
                  <a href={`https://transport.yunniuji.com/enterprise/orders/${transit.id}`} target="_blank" rel="noreferrer">在途监管平台</a>
                  查看完整轨迹与检疫信息
                </div>
              </div>
            ) : (
              <div className="order-transit__empty">
                {order.order_status === 3 ? (
                  <>
                    <span>该订单尚未发起在途运输</span>
                    <button className="btn btn-primary btn-sm" onClick={handleStartTransit}>
                      发起运输（在途监管）
                    </button>
                  </>
                ) : (
                  <span>交易完成后可发起在途监管运输单（跨区域完整流程）</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ④ 操作区 */}
      <div className="order-actions">
        {actionError ? <div className="form-error">{actionError}</div> : null}
        {order.order_status === 0 && isBuyer ? (
          <>
            {order.expire_at ? (
              <div className="order-lock">
                <Countdown endTime={order.expire_at} prefix="付款剩余" expiredText="订单已自动取消" onExpire={load} />
                <span>下单后 {LOCK_MINUTES} 分钟内未支付将自动取消并释放牛只（IR-05/06）</span>
              </div>
            ) : null}
            <button className="btn btn-primary btn-lg" disabled={busy} onClick={openPayModal}>
              {busy ? '支付中…' : '确认支付'}
            </button>
            <button className="btn btn-ghost btn-lg" disabled={busy} onClick={handleCancel}>
              取消订单
            </button>
            <span className="order-actions__tip">支付通道繁忙？可尝试对公转账备用渠道（IR-12）</span>
          </>
        ) : null}

        {order.order_status === 1 && isSeller ? (
          <>
            <button className="btn btn-primary btn-lg" disabled={busy} onClick={handleShip}>
              {busy ? '提交中…' : '安排发货（已付款 → 已发货）'}
            </button>
            <span className="order-actions__tip">买家付款后请安排发货，买家确认交割后平台解付（F1）</span>
          </>
        ) : null}

        {order.order_status === 2 && isSeller ? (
          <div className="order-actions__tip">
            请通知买家在平台确认交割（上传 ≥3 张凭证 + GPS）
          </div>
        ) : null}

        {order.order_status === 3 ? (
          <>
            {reviewOpen ? (
              <button className="btn btn-gold btn-lg" onClick={() => setShowReview(true)}>
                去评价（完成后 15 天内）
              </button>
            ) : (
              <span className="order-actions__tip">评价窗口已关闭，系统已按默认好评处理（IR-15）</span>
            )}
            {isBuyer && !settled ? (
              <button className="btn btn-outline btn-lg" disabled={busy} onClick={() => setShowAfterSales(true)}>
                申请售后
              </button>
            ) : null}
            {settled ? (
              <span className="order-actions__tip">本订单已完成平台财务结算</span>
            ) : null}
          </>
        ) : null}

        {order.order_status === 4 ? (
          <div className="order-actions__tip">
            订单已取消（{order.cancel_reason || '未说明原因'}）。如为付款后卖家违约，平台将全额退款并记录违约（IR-10）
          </div>
        ) : null}

        {order.order_status === 5 ? (
          <div className="order-actions__tip">订单已退款，资金已原路退回买家托管账户</div>
        ) : null}

        {order.order_status === 6 ? (
          <div className="order-actions__tip">订单已超时自动关闭（下单 {LOCK_MINUTES} 分钟内未支付），牛只已释放</div>
        ) : null}

        {order.order_status === 7 ? (
          <>
            <div className="order-actions__tip">
              售后处理中：{order.after_sales_reason || '原因未填写'}（开启于 {formatDateTime(order.after_sales_opened_at)}）
            </div>
            {isBuyer ? (
              <button className="btn btn-outline btn-lg" disabled={busy} onClick={handleCloseAfterSales}>
                {busy ? '提交中…' : '撤回售后申请'}
              </button>
            ) : (
              <span className="order-actions__tip">平台处理完成前订单暂停结算与评价</span>
            )}
          </>
        ) : null}
      </div>

      {/* 评价弹窗 */}
      {showReview ? (
        <div className="modal-mask" onClick={() => setShowReview(false)}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <span className="modal-title">评价交易（{isBuyer ? '买家评卖家' : '卖家评买家'}）</span>
              <button className="modal-close" onClick={() => setShowReview(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label"><span className="req">*</span>评分</label>
                <div className="form-radio-row">
                  {[1, 2, 3, 4, 5].map((v) => (
                    <label className={`review-star ${rating === v ? 'active' : ''}`} key={v}>
                      <input type="radio" name="rating" checked={rating === v} onChange={() => setRating(v)} />
                      {'★'.repeat(v)}{'☆'.repeat(5 - v)}
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">评价内容</label>
                <textarea
                  className="form-control"
                  rows={3}
                  maxLength={512}
                  placeholder="说说本次交易体验（选填，最多 512 字）"
                  value={reviewContent}
                  onChange={(e) => setReviewContent(e.target.value)}
                />
              </div>
              <div className="form-help">一单一评，提交后不可修改/删除；完成后 15 天内有效（IR-15）</div>
            </div>
            <div className="modal-foot">
              <button className="btn btn-ghost" onClick={() => setShowReview(false)}>取消</button>
              <button
                className="btn btn-gold"
                disabled={busy || reviewSubmitting}
                onClick={() => {
                  setReviewSubmitting(true);
                  handleSubmitReview();
                }}
              >
                {busy || reviewSubmitting ? '提交中…' : '提交评价'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* 售后弹窗（P0-5：买家在已完成订单申请售后） */}
      {showAfterSales ? (
        <div className="modal-mask" onClick={() => !afterSalesSubmitting && setShowAfterSales(false)}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <span className="modal-title">申请售后</span>
              <button className="modal-close" onClick={() => setShowAfterSales(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label"><span className="req">*</span>售后原因</label>
                <textarea
                  className="form-control"
                  rows={4}
                  maxLength={512}
                  placeholder="请描述遇到的问题（至少 5 个字，最多 512 字）"
                  value={afterSalesReason}
                  onChange={(e) => setAfterSalesReason(e.target.value)}
                />
              </div>
              <div className="form-help">
                售后开启后订单进入「售后中」，处理完成前暂停结算与评价；买家可随时撤回申请
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn btn-ghost" disabled={busy} onClick={() => setShowAfterSales(false)}>取消</button>
              <button
                className="btn btn-primary"
                disabled={busy || afterSalesSubmitting}
                onClick={() => {
                  setAfterSalesSubmitting(true);
                  handleOpenAfterSales();
                }}
              >
                {busy || afterSalesSubmitting ? '提交中…' : '提交售后申请'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* 支付弹层（资金 2FA · 支付密码；N3 前端闭环） */}
      <PaymentPasswordModal
        open={showPay}
        title="确认支付"
        desc={`订单 ${order.order_no} · 实付 ${formatMoney(order.paid_amount || order.total_amount + order.service_fee)}`}
        submitting={busy}
        error={payError}
        showSetupHint={payPwdNotSet}
        onCancel={() => !busy && setShowPay(false)}
        onSubmit={handlePay}
        onGoSetup={() => navigate('/my?tab=security')}
      />

      {/* 合同预览 */}
      <ContractPreview
        open={showContract}
        onClose={() => setShowContract(false)}
        title={`电子合同 ${contractNo}`}
        footer={
          <button className="btn btn-primary" onClick={() => setShowContract(false)}>关闭</button>
        }
      >
        <h4>云牛集牛只电子买卖合同</h4>
        <p>合同编号：{contractNo}</p>
        <p>卖方：{order.seller?.enterprise_name || order.seller?.real_name || '-'}</p>
        <p>买方：{order.buyer?.real_name || order.buyer?.username || '-'}</p>
        <p>牛只：{cattle?.breed} · {cattle?.ear_tag_no}，数量 {item?.quantity} 头，单价 {formatMoney(item?.unit_price)}</p>
        <p>金额：牛只价款 {formatMoney(order.total_amount)} 元，平台服务费 {formatMoney(order.service_fee)} 元，合计 {contractAmount}</p>
        <p>交割：{DELIVERY_METHOD[order.delivery_method ?? 1]} · {order.delivery_address ?? '-'} · {formatDateTime(order.delivery_time)}</p>
        <p>资金监管：买方付款进入平台监管账户，交割确认后解付卖方。</p>
        {contract?.content ? (
          <div className="contract-content" style={{ whiteSpace: 'pre-wrap', fontSize: 13, color: '#555', marginTop: 8 }}>
            {contract.content}
          </div>
        ) : null}
        <p>存证：本合同已上链存证，存证编号 {contractSignTx || `CERT-${order.order_no}`}。</p>
        <div className="contract-sign">
          合同状态：{contractStatus.label} · 生成时间：{formatDateTime(contract?.created_at ?? order.created_at)}
          {contract ? ` · 版本 v${contract.version}` : ' · 正式合同请在交易完成后由平台生成'}
        </div>
      </ContractPreview>
    </div>
  );
}
